/**
 * Live rider-GPS WebSocket helpers.
 *
 * Two hooks paired with the backend routes in /app/backend/routes_ws.py:
 *
 *   useRiderGpsPusher(riderId, opts)
 *     Opens a WebSocket on /api/ws/rider/{rider_id}/location and pushes
 *     {lat, lng, accuracy, ts} every `pushIntervalMs` while the device
 *     reports usable geolocation. Persists the last point through the
 *     existing HTTP fallback (`/api/delivery/me/gps`) so older clients
 *     and the customer-side polling code keep working unchanged.
 *
 *   useOrderTracking(orderId, opts)
 *     Customer-side subscriber on /api/ws/order/{order_id}/track. Returns
 *     the live `{lat, lng, ts, accuracy}` for the rider assigned to that
 *     order. Falls back to HTTP polling (`/api/customer/orders/:id/rider`)
 *     when the WS handshake fails — so the existing tracking UI still
 *     works behind proxies that strip WebSocket upgrades.
 */
import { useEffect, useRef, useState } from "react";
import api from "./api";
import { getToken as getStoredToken } from "./authStorage";
import { BACKEND_URL } from "./backendUrl";

function wsUrlFor(path) {
  const base = BACKEND_URL || "";
  if (!base) return "";
  // /api/ws/... stays under the same /api prefix the ingress routes to
  // FastAPI; we just switch http(s) → ws(s).
  return base.replace(/^http/, "ws") + path;
}

function getToken() {
  try { return getStoredToken(); } catch { return null; }
}

/* ------------------------ Rider side ------------------------ */
export function useRiderGpsPusher(riderId, {
  enabled = true,
  pushIntervalMs = 5000,
  fallbackLat = 17.42,
  fallbackLng = 78.41,
} = {}) {
  const wsRef = useRef(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!enabled || !riderId) return undefined;
    if (!getToken()) return undefined;

    let alive = true;
    let watchId = null;
    let pollTimer = null;
    let currentPos = { lat: fallbackLat, lng: fallbackLng, accuracy: null };

    // Open the WS first; HTTP fallback layer always runs regardless so the
    // rider's location stays fresh even if the WS handshake fails.
    // Re-reads the token on EVERY (re)connect attempt, not just once at
    // mount — a silent access-token refresh elsewhere (lib/api.js, on a
    // long-lived rider session) would otherwise leave this loop retrying
    // forever with the old, now-rejected token instead of picking up the
    // new one on its next 5s reconnect.
    const openWs = () => {
      const token = getToken();
      if (!token) { if (alive) setTimeout(openWs, 5000); return; }
      try {
        const url = wsUrlFor(`/api/ws/rider/${encodeURIComponent(riderId)}/location?token=${encodeURIComponent(token)}`);
        const ws = new WebSocket(url);
        ws.onclose = () => { if (alive) setTimeout(openWs, 5000); };  // simple reconnect
        ws.onerror = () => { /* let onclose handle reconnect */ };
        wsRef.current = ws;
      } catch (_e) {
        // network gone — try again after the next push tick
      }
    };
    openWs();

    // Geolocation: use watchPosition where available, otherwise a small
    // jitter around the last persisted position so the existing "rider
    // online" indicator keeps flashing on the admin map.
    const onPos = (pos) => {
      currentPos = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      };
    };
    if (typeof navigator !== "undefined" && navigator.geolocation?.watchPosition) {
      try {
        watchId = navigator.geolocation.watchPosition(onPos, () => {}, {
          enableHighAccuracy: true, maximumAge: 5000, timeout: 10000,
        });
      } catch { /* permission denied or unsupported */ }
    }

    const push = () => {
      const now = Date.now();
      if (now - lastSentRef.current < pushIntervalMs - 200) return;
      lastSentRef.current = now;
      const payload = { ...currentPos, ts: new Date().toISOString() };
      // Prefer WS — falls back to HTTP if socket isn't OPEN.
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify(payload)); return; } catch { /* fall through */ }
      }
      api.post("/api/delivery/me/gps", { lat: payload.lat, lng: payload.lng })
        .catch(() => { /* swallow — next tick will retry */ });
    };

    push();                                  // immediate first push so the marker appears
    pollTimer = setInterval(push, pushIntervalMs);

    return () => {
      alive = false;
      clearInterval(pollTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
      if (watchId != null && navigator.geolocation?.clearWatch) {
        try { navigator.geolocation.clearWatch(watchId); } catch { /* noop */ }
      }
    };
  }, [riderId, enabled, pushIntervalMs, fallbackLat, fallbackLng]);
}

/* ------------------------ Customer side ------------------------ */
export function useOrderTracking(orderId, { pollFallbackMs = 8000 } = {}) {
  const [pos, setPos] = useState(null);          // {lat, lng, ts, accuracy, rider_name}
  const [status, setStatus] = useState("connecting");  // 'connecting'|'live'|'polling'|'closed'

  useEffect(() => {
    if (!orderId) return undefined;
    const token = getToken();

    let alive = true;
    let ws = null;
    let pollTimer = null;

    const startPolling = () => {
      if (pollTimer) return;
      setStatus("polling");
      const tick = () => api
        .get(`/api/customer/orders/${orderId}/rider`)
        .then(({ data }) => {
          if (!alive || !data?.delivery_boy) return;
          const r = data.delivery_boy;
          if (r.current_lat != null) {
            setPos({
              lat: r.current_lat, lng: r.current_lng,
              ts: r.current_ts, rider_name: r.name,
            });
          }
        })
        .catch(() => { /* ignore */ });
      tick();
      pollTimer = setInterval(tick, pollFallbackMs);
    };

    if (token) {
      try {
        ws = new WebSocket(wsUrlFor(
          `/api/ws/order/${encodeURIComponent(orderId)}/track?token=${encodeURIComponent(token)}`,
        ));
        ws.onopen = () => alive && setStatus("live");
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.keepalive) return;
            if (m.lat != null && m.lng != null) {
              setPos({
                lat: m.lat, lng: m.lng, ts: m.ts,
                accuracy: m.accuracy, rider_name: m.rider_name,
              });
            }
          } catch { /* ignore */ }
        };
        ws.onerror = () => { /* close will fire next */ };
        ws.onclose = () => {
          if (!alive) return;
          // If the server explicitly told us "no rider yet" (4409), don't
          // hammer the fallback — just stay closed.
          startPolling();
        };
      } catch {
        startPolling();
      }
    } else {
      startPolling();
    }

    return () => {
      alive = false;
      try { ws?.close(); } catch { /* noop */ }
      if (pollTimer) clearInterval(pollTimer);
      setStatus("closed");
    };
  }, [orderId, pollFallbackMs]);

  return { position: pos, status };
}
